import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MEMBERSHIP_TYPES, TENURES, BLUE_TENURES, FAMILY_MEMBER_COUNTS } from "@/lib/constants";
import { toast } from "sonner";

const blank = { visitor_id: "", lead_id: null, membership_type: "Gold Membership", tenure: "12 Months", family_members: "1", amount: "", sales_executive: "", notes: "" };

const tenureFor = (m) => (m === "Blue Membership" ? "15 Years" : "12 Months");

export const ConversionDialog = ({ open, onClose, initial, team, onSaved, title = "Record Conversion" }) => {
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (open) {
      const m = initial?.membership_type || blank.membership_type;
      setForm({
        ...blank, ...Object.fromEntries(Object.entries(initial || {}).filter(([, v]) => v !== null && v !== undefined)),
        membership_type: m, tenure: initial?.tenure || tenureFor(m),
        family_members: String(initial?.family_members || 1), amount: initial?.amount ? String(initial.amount) : "",
      });
    }
  }, [open, initial]);

  const isBlue = form.membership_type === "Blue Membership";
  const showTenure = form.membership_type !== "Gold Membership";
  const showFamily = form.membership_type === "Other";
  const tenureOptions = isBlue ? BLUE_TENURES : TENURES;

  const save = async () => {
    try {
      await api.post("/conversions", {
        visitor_id: form.visitor_id, lead_id: form.lead_id || null, membership_type: form.membership_type,
        amount: parseFloat(form.amount || 0), sales_executive: form.sales_executive || null, notes: form.notes || null,
        tenure: showTenure ? form.tenure : null,
        family_members: showFamily ? parseInt(form.family_members || 1) : null,
      });
      toast.success("Sale recorded in Conversions"); onClose(); onSaved?.();
    } catch { toast.error("Visitor ID not found (use OV-000001 format)"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="conversion-dialog"><DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-sm font-semibold">Visitor ID</label><Input value={form.visitor_id} disabled={!!initial?.visitor_id} onChange={(e) => setForm({ ...form, visitor_id: e.target.value.toUpperCase() })} placeholder="OV-000001" data-testid="conv-visitor-input" /></div>
          <div><label className="text-sm font-semibold">Membership Type</label>
            <Select value={form.membership_type} onValueChange={(v) => setForm({ ...form, membership_type: v, tenure: tenureFor(v), family_members: "1" })}>
              <SelectTrigger data-testid="conv-membership-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MEMBERSHIP_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(showTenure || showFamily) && (
            <div className={`grid gap-3 ${showTenure && showFamily ? "grid-cols-2" : "grid-cols-1"}`}>
              {showTenure && (
                <div><label className="text-sm font-semibold">Tenure</label>
                  <Select value={form.tenure} onValueChange={(v) => setForm({ ...form, tenure: v })}>
                    <SelectTrigger data-testid="conv-tenure-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{tenureOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {showFamily && (
                <div><label className="text-sm font-semibold">Family Members</label>
                  <Select value={form.family_members} onValueChange={(v) => setForm({ ...form, family_members: v })}>
                    <SelectTrigger data-testid="conv-family-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{FAMILY_MEMBER_COUNTS.map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "member" : "members"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <div><label className="text-sm font-semibold">Amount (₹)</label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="25000" data-testid="conv-amount-input" /></div>
          <div><label className="text-sm font-semibold">Sales Executive</label>
            <Select value={form.sales_executive || ""} onValueChange={(v) => setForm({ ...form, sales_executive: v })}>
              <SelectTrigger data-testid="conv-exec-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{team.map((t) => <SelectItem key={t.member_id} value={t.member_id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes…" rows={2} data-testid="conv-notes-textarea" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.visitor_id || !form.amount} data-testid="conv-save-btn" className="bg-emerald-600 hover:bg-emerald-700">Save Sale</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
